import {
  UseComboboxReturnValue as UseDownshiftComboboxReturnValue,
  useCombobox as useDownshiftCombobox,
} from "downshift"
import { useEffect, useMemo, useRef, useState } from "react"

import { filterItems } from "./filterItems"

export interface UseComboboxProps<T> {
  items: T[]
  keyExtractor: (item: T) => string
  valueExtractor: (item: T) => string
  onSelect: (item: T) => void
  autoFilter?: boolean
  onChangeQuery?: (query: string) => void
  maxRenderedItems?: number
  inline?: boolean
}

export interface UseComboboxResult<T>
  extends UseDownshiftComboboxReturnValue<T> {
  filteredItems: T[]
  inline: boolean
}

export function useCombobox<T>({
  items,
  keyExtractor,
  valueExtractor,
  onSelect,
  autoFilter,
  onChangeQuery,
  maxRenderedItems,
  inline = false,
}: UseComboboxProps<T>): UseComboboxResult<T> {
  const [filterInputValue, setFilterInputValue] = useState("")

  const filteredItems = useMemo(() => {
    const filtered = filterItems({
      items,
      filterInputValue,
      valueExtractor,
      autoFilter,
    })
    return maxRenderedItems !== undefined && filtered.length > maxRenderedItems
      ? filtered.slice(0, maxRenderedItems)
      : filtered
  }, [items, filterInputValue, valueExtractor, autoFilter, maxRenderedItems])

  function handleKeyExtractor(item: T | null): string {
    return item ? keyExtractor(item) : ""
  }

  function handleItemToString(item: T | null): string {
    return item ? valueExtractor(item) : ""
  }

  return {
    ...useDownshiftCombobox({
      items: filteredItems,
      onSelectedItemChange({ selectedItem }) {
        onSelect(selectedItem)
      },
      itemToString: handleItemToString,
      itemToKey: handleKeyExtractor,
      defaultHighlightedIndex: 0,
      onInputValueChange(changes) {
        setFilterInputValue(changes.inputValue)
        onChangeQuery?.(changes.inputValue)
      },
      initialIsOpen: !inline,
    }),
    filteredItems,
    inline,
  }
}

export function useMultiCombobox<T>({
  items,
  keyExtractor,
  valueExtractor,
  onSelect,
  autoFilter,
  onChangeQuery,
  maxRenderedItems,
  inline = false,
}: UseComboboxProps<T>): UseComboboxResult<T> {
  const [filterInputValue, setFilterInputValue] = useState("")

  // The local input resets to "" on every remount (e.g. popover re-open), but
  // any externally-hosted query state hosted by the consumer (useSearchSalesV2,
  // useOmniSearch, etc.) persists. Sync the upstream query so the dropdown
  // doesn't render results from a previous session's input. A ref lets us fire
  // exactly once on mount without capturing a stale onChangeQuery closure.
  const onChangeQueryRef = useRef(onChangeQuery)
  onChangeQueryRef.current = onChangeQuery
  useEffect(() => {
    onChangeQueryRef.current?.("")
  }, [])

  const filteredItems = useMemo(() => {
    const filtered = filterItems({
      items,
      filterInputValue,
      valueExtractor,
      autoFilter,
    })
    return maxRenderedItems !== undefined && filtered.length > maxRenderedItems
      ? filtered.slice(0, maxRenderedItems)
      : filtered
  }, [items, filterInputValue, valueExtractor, autoFilter, maxRenderedItems])

  function handleKeyExtractor(item: T | null): string {
    return item ? keyExtractor(item) : ""
  }

  function handleItemToString(item: T | null): string {
    return item ? valueExtractor(item) : ""
  }

  return {
    ...useDownshiftCombobox({
      inputValue: filterInputValue,
      items: filteredItems,
      selectedItem: null,
      onSelectedItemChange({ selectedItem }) {
        onSelect(selectedItem)
      },
      itemToString: handleItemToString,
      itemToKey: handleKeyExtractor,
      defaultHighlightedIndex: 0,
      onInputValueChange(changes) {
        switch (changes.type) {
          case useDownshiftCombobox.stateChangeTypes.ItemClick:
          case useDownshiftCombobox.stateChangeTypes.InputKeyDownEnter:
          case useDownshiftCombobox.stateChangeTypes
            .ControlledPropUpdatedSelectedItem:
            return
        }

        setFilterInputValue(changes.inputValue)
        onChangeQuery?.(changes.inputValue)
      },
      stateReducer: (state, actionAndChanges) => {
        const { changes, type } = actionAndChanges
        switch (type) {
          case useDownshiftCombobox.stateChangeTypes.InputKeyDownEnter:
          case useDownshiftCombobox.stateChangeTypes.ItemClick:
            return {
              ...changes,
              isOpen: inline ? state.isOpen : true,
              highlightedIndex: state.highlightedIndex,
              inputValue: "",
            }
          case useDownshiftCombobox.stateChangeTypes.InputBlur:
            return {
              ...changes,
              inputValue: inline ? state.inputValue : "",
            }
          default:
            return changes
        }
      },
      initialIsOpen: !inline,
      isOpen: inline ? undefined : true,
    }),
    filteredItems,
    inline,
  }
}
